import React, { createContext, useContext, useState, useCallback } from 'react';
import de from './translations/de';
import en from './translations/en';

const translations = { de, en };

// localStorage key for the language preference. Renamed from the
// pre-rebrand `reeltypost_lang` key. We read both on initial load so
// existing users don't lose their pref, then write only the new key
// going forward. Once the legacy key has aged out (everyone has logged
// in once after this deploy), the migration block below can go.
const LANG_STORAGE_KEY = 'real-content_lang';
const LEGACY_LANG_KEY = 'reeltypost_lang';

function readInitialLang() {
  try {
    const current = localStorage.getItem(LANG_STORAGE_KEY);
    if (current) return current;
    const legacy = localStorage.getItem(LEGACY_LANG_KEY);
    if (legacy) {
      // One-shot migrate: copy then drop the old key so we don't leak
      // stale state forward.
      localStorage.setItem(LANG_STORAGE_KEY, legacy);
      localStorage.removeItem(LEGACY_LANG_KEY);
      return legacy;
    }
  } catch (_) {
    // Private mode / quota exceeded. Fall through to default.
  }
  return 'de';
}

const I18nContext = createContext();

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(readInitialLang);

  const setLanguage = useCallback((newLang) => {
    setLang(newLang);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, newLang);
    } catch (_) {
      /* private mode. Best effort */
    }
  }, []);

  const t = useCallback((key, params = {}) => {
    const keys = key.split('.');
    let value = translations[lang];
    for (const k of keys) {
      value = value?.[k];
    }

    // ICU-style pluralization. When a translation entry is an object
    // shaped like `{ one: '...', other: '...' }` and the caller passes
    // `params.count`, we pick the matching plural form using the
    // browser's Intl.PluralRules. Handles English's two-form rule
    // and German's two-form rule equally well, and lets us support
    // any future locale that has more forms (Polish, Russian, etc.)
    // without changing this code.
    //
    // Plain string entries (the common case) sail through unchanged.
    if (
      value && typeof value === 'object' && !Array.isArray(value) &&
      typeof params.count === 'number'
    ) {
      try {
        const locale = lang === 'de' ? 'de-AT' : 'en-US';
        const rule = new Intl.PluralRules(locale).select(params.count);
        value = value[rule] ?? value.other ?? value.one ?? key;
      } catch (_) {
        value = value.other ?? value.one ?? key;
      }
    }

    if (value === undefined || value === null) return key;
    return Object.entries(params).reduce(
      (str, [param, val]) => str.replace(`{{${param}}}`, val),
      String(value)
    );
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useTranslation = () => useContext(I18nContext);
